import { Injectable, inject } from '@angular/core';
import { map, Observable, of, switchMap, catchError, shareReplay } from 'rxjs';
import { Delivery } from 'src/app/bo/delivery';
import { Equipment } from 'src/app/bo/equipment';
import { DrillActivityService, WeekBounds } from './drill-activity.service';
import { DeliveriesService } from './deliveries.service';
import { HttpService } from './http.service';
import { ShiftConfig } from 'src/app/shared/models/drilling.models';
import { DeliveryActivityItem, DeliveryBlastRow } from 'src/app/shared/models/delivery-activity.models';

@Injectable({ providedIn: 'root' })
export class DeliveryActivityService {
  private readonly deliveriesService = inject(DeliveriesService);
  private readonly http = inject(HttpService);
  private readonly equipmentBySiteCache = new Map<number, { expiresAt: number; stream: Observable<Equipment[]> }>();
  private readonly equipmentBySiteTtlMs = 4 * 60 * 60 * 1000;

  getCurrentWeekBounds(): WeekBounds {
    const today = new Date();
    const dow = today.getDay();
    const daysToMonday = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(today);
    monday.setDate(today.getDate() + daysToMonday);
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    return { start: monday, end: sunday };
  }

  /**
   * @param timezone Fuseau du site au format "UTC+HH:MM". Les heures sont stockées en UTC :
   *                 sans lui, les livraisons seraient réparties selon le fuseau du navigateur.
   */
  getDeliveryRowsForWeek(siteId: number, bounds: WeekBounds, config: ShiftConfig, timezone?: string): Observable<DeliveryBlastRow[]> {
    // `bounds` marque des dates calendaires (voir l'URL, qui ne porte qu'un "YYYY-MM-DD"),
    // pas un instant précis du navigateur. Pour un site dont le décalage UTC diffère de
    // celui du navigateur, une requête bornée strictement sur bounds.start/end peut donc
    // exclure des livraisons dont le StartTime (UTC) tombe la veille ou le lendemain une
    // fois ramené à l'heure du site. On élargit la requête d'une journée de chaque côté,
    // puis on refiltre le résultat sur les dates calendaires demandées, cette fois-ci
    // calculées à l'heure locale du site.
    const startIso = DeliveryActivityService.toCalendarDateString(bounds.start);
    const endIso = DeliveryActivityService.toCalendarDateString(bounds.end);
    const queryStart = new Date(bounds.start.getTime() - 24 * 60 * 60 * 1000);
    const queryEnd = new Date(bounds.end.getTime() + 24 * 60 * 60 * 1000);

    return this.deliveriesService.getDeliveriesBySiteId(siteId, queryStart, queryEnd).pipe(
      catchError(() => of([] as Delivery[])),
      switchMap(deliveries => {
        if (deliveries.length === 0) {
          return of([] as DeliveryBlastRow[]);
        }

        return this.getEquipmentBySite(siteId).pipe(
          map(equipment => {
            const equipmentById = new Map<number, Equipment>(
              equipment.filter(e => e.equipementId != null).map(e => [e.equipementId!, e]),
            );

            return this.buildRows(deliveries, equipmentById, config, timezone, startIso, endIso);
          }),
        );
      }),
    );
  }

  clearEquipmentBySiteCache(siteId?: number): void {
    if (siteId == null) {
      this.equipmentBySiteCache.clear();
      return;
    }

    this.equipmentBySiteCache.delete(siteId);
  }

  private getEquipmentBySite(siteId: number): Observable<Equipment[]> {
    const cached = this.equipmentBySiteCache.get(siteId);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.stream;
    }

    const request$ = this.http.get<Equipment[]>(`Sites/${siteId}/Equipment`).pipe(
      catchError(() => of([] as Equipment[])),
      shareReplay(1),
    );

    this.equipmentBySiteCache.set(siteId, {
      expiresAt: Date.now() + this.equipmentBySiteTtlMs,
      stream: request$,
    });

    return request$;
  }

  private buildRows(
    deliveries: Delivery[],
    equipmentById: Map<number, Equipment>,
    config: ShiftConfig,
    timezone: string | undefined,
    startIso: string,
    endIso: string,
  ): DeliveryBlastRow[] {
    const rowsByBlast = new Map<number, DeliveryBlastRow>();

    for (const delivery of deliveries) {
      if (!delivery.tirId || !delivery.startTime) {
        continue;
      }

      const row = rowsByBlast.get(delivery.tirId) ?? {
        blastId: delivery.tirId,
        projectId: Number(delivery.projectId ?? 0),
        label: delivery.blastLabel?.trim() || `Blast ${delivery.tirId}`,
        zone: delivery.zoneLabel ?? '',
        targetKg: 0,
        blastHoleCount: Number(delivery.blastHoleCount ?? 0),
        deliveries: [],
      };

      if (row.projectId === 0 && delivery.projectId) {
        row.projectId = delivery.projectId;
      }

      if (!row.zone && delivery.zoneLabel) {
        row.zone = delivery.zoneLabel;
      }

      if ((!row.label || row.label.startsWith('Blast ')) && delivery.blastLabel) {
        row.label = delivery.blastLabel;
      }

      if (row.blastHoleCount === 0 && delivery.blastHoleCount) {
        row.blastHoleCount = delivery.blastHoleCount;
      }

      const item = this.mapDeliveryToItem(delivery, config, equipmentById, timezone);
      // La requête a été élargie d'une journée de chaque côté pour ne rater aucune
      // livraison proche d'une frontière de fuseau ; on ne garde ici que celles dont la
      // date, une fois ramenée à l'heure du site, tombe réellement dans la période demandée.
      if (!item || item.date < startIso || item.date > endIso) {
        continue;
      }

      row.deliveries.push(item);
      rowsByBlast.set(delivery.tirId, row);
    }

    return [...rowsByBlast.values()]
      .map(row => ({
        ...row,
        deliveries: row.deliveries.sort((a, b) => a.startTime.localeCompare(b.startTime)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private mapDeliveryToItem(delivery: Delivery, config: ShiftConfig, equipmentById: Map<number, Equipment>, timezone?: string): DeliveryActivityItem | null {
    const startUtc = new Date(delivery.startTime);
    if (Number.isNaN(startUtc.getTime())) {
      return null;
    }

    // Jour et poste se lisent à l'heure murale du site, pas à celle du navigateur.
    const siteLocal = DrillActivityService.toSiteLocal(startUtc, timezone);

    const equipmentLabel = this.resolveEquipmentLabel(delivery, equipmentById);
    const operatorLabel = this.resolveOperatorLabel(delivery);

    return {
      id: delivery.deliveryId,
      blastId: delivery.tirId,
      equipmentId: this.resolveEquipmentId(delivery),
      equipmentLabel,
      operatorId: this.resolveOperatorId(delivery, operatorLabel),
      operatorLabel,
      date: DeliveryActivityService.toIsoDateString(siteLocal),
      shiftIndex: DrillActivityService.resolveShiftIndex(siteLocal.getUTCHours(), config.shiftStartHours),
      loadedKg: Number(delivery.kgDelivered ?? 0),
      holeCount: Number(delivery.holeCount ?? 0),
      startTime: delivery.startTime,
      endTime: delivery.endTime ?? null,
    };
  }

  private parseMemuIds(memuId: string | null | undefined): number[] {
    if (!memuId?.trim()) {
      return [];
    }
    return memuId.trim().slice(1, -1).trim().split(',').map(s => parseInt(s.trim(), 10)).filter(n => n > 0);
  }

  private resolveEquipmentLabel(delivery: Delivery, equipmentById: Map<number, Equipment>): string {
    const ids = this.parseMemuIds(delivery.memuId);
    const names = ids.map(id => equipmentById.get(id)?.name).filter((n): n is string => !!n);
    return names.length > 0 ? names.join(', ') : 'No equipment';
  }

  private resolveOperatorLabel(delivery: Delivery): string {
    const labels = delivery.operatorsNames
      ?.split(',')
      .map(name => name.trim())
      .filter(name => name.length > 0);

    if (labels && labels.length > 0) {
      return labels[0];
    }

    return 'Unknown operator';
  }

  private resolveOperatorId(delivery: Delivery, fallbackLabel: string): number {
    const operatorId = delivery.operators?.[0];
    if (operatorId && operatorId > 0) {
      return operatorId;
    }

    return this.hashAsPositiveInt(fallbackLabel);
  }

  private resolveEquipmentId(delivery: Delivery): number {
    const ids = this.parseMemuIds(delivery.memuId);
    return ids.length > 0 ? ids[0] : this.hashAsPositiveInt(delivery.memuId ?? '');
  }

  /**
   * Formate une date « faux-UTC » produite par {@link DrillActivityService.toSiteLocal},
   * dont les champs UTC portent l'heure murale du site. Lire les champs locaux
   * réappliquerait le décalage du navigateur.
   */
  private static toIsoDateString(siteLocal: Date): string {
    const y = siteLocal.getUTCFullYear();
    const m = String(siteLocal.getUTCMonth() + 1).padStart(2, '0');
    const day = String(siteLocal.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /** Formate la date calendaire portée par `bounds` (voir getDeliveryRowsForWeek), en heure navigateur. */
  private static toCalendarDateString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private hashAsPositiveInt(value: string): number {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      hash = ((hash << 5) - hash) + value.charCodeAt(i);
      hash |= 0;
    }

    return Math.abs(hash) || 1;
  }
}
